import type { FunctionalComponent } from 'preact';
import { useEffect } from 'preact/hooks';
import { closeModal } from '@/store/ui.slice.ts';
import { resolveDialog } from '@/store/dialog.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';

/**
 * askAlert / askConfirm 이 띄우는 공용 모달.
 *
 * 구 프로젝트는 메시지를 dangerouslySetInnerHTML 로 그렸다. `askAlert(response.message)`
 * 처럼 서버 문자열을 그대로 넘기는 호출부가 있어 XSS 통로가 됐다.
 * 여기서는 항상 텍스트로 그린다. 줄바꿈은 `\n` 으로 넣으면 된다
 * (`.popup-message` 에 white-space: pre-line).
 *
 * reward.save / watch.video / leave.video 는 재생 화면과 함께 이식한다.
 */
export const Modal: FunctionalComponent = () => {
  const dispatch = useAppDispatch();
  const { isOpen, modalType, modalProps } = useAppSelector((state) => state.ui);

  const answer = (result: boolean) => {
    resolveDialog(result);
    dispatch(closeModal());
  };

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') answer(false);
      if (e.key === 'Enter' && modalType === 'alert') answer(true);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // answer 는 매 렌더 새로 만들어지지만 dispatch 는 안정적이라 재등록해도 무해하다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modalType]);

  if (!isOpen) return null;

  const message = modalProps?.message ?? '';
  const isConfirm = modalType === 'confirm' || modalType === 'leave.video';

  /*
    ★ **다이얼로그는 언제나 맨 위여야 한다.** (2026-09-16 — `popup-overlay--dialog`)

    예전에는 페이지 팝업과 똑같이 `popup-overlay` 만 썼다. 둘 다 z-index 200 이라 같은
    층이고, 같으면 DOM 에서 뒤에 있는 것이 위로 온다. `<Modal />` 은 app.tsx 에서
    `<Router>` **앞**에 있어 항상 페이지 팝업 밑에 깔렸다.

    증상: 마이페이지 '이미 진행중인 신청건이 있습니다' 팝업에서 '신청 취소' 를 누르면
    확인창이 그 뒤에 가려 보이지 않았다. 사용자는 화면이 멈춘 줄 안다. 같은 구조가
    이벤트·정보·계좌·출금 화면에도 있어 한 곳만 고칠 일이 아니다.
  */
  return (
    <div
      class="popup-overlay popup-overlay--dialog show"
      onClick={(e) => {
        // 배경 클릭은 취소로 본다.
        if (e.target === e.currentTarget) answer(false);
      }}
    >
      <div class="popup-sheet">
        {/* 적립 완료 — 시안 play 흐름의 보상 팝업 */}
        {modalType === 'reward.save' ? (
          <div class="popup-body popup-body--warning">
            <h3 class="popup-title">포인트가 적립되었습니다.</h3>
            <div class="popup-point-badge">
              <img src="/images/point.png" alt="" />
              <span>{message}P가 지급되었습니다.</span>
            </div>
          </div>
        ) : modalType === 'leave.video' ? (
          <div class="popup-body popup-body--warning">
            <div class="popup-icon">
              <img src="/images/warning.png" alt="" />
            </div>
            <h3 class="popup-title">지금 나가면 포인트가 지급되지 않아요.</h3>
            <p class="popup-desc">처음부터 다시 시청해야 합니다. 페이지를 이동하시겠습니까?</p>
          </div>
        ) : (
          <div class="popup-body">
            <p class="popup-desc popup-message">
              {message || (isConfirm ? '진행할까요?' : '알림')}
            </p>
          </div>
        )}

        {isConfirm ? (
          <div class="popup-btn-group">
            <button type="button" class="popup-btn" onClick={() => answer(false)}>
              아니오
            </button>
            <button type="button" class="popup-btn confirm" onClick={() => answer(true)}>
              확인
            </button>
          </div>
        ) : (
          <button type="button" class="popup-btn" onClick={() => answer(true)}>
            확인
          </button>
        )}
      </div>
    </div>
  );
};
